import React, { type ComponentProps, useState } from "react";

import type { PolicyMetaData } from "@/core/types";

import { ConsentDisclaimer } from "@/components/Consent/ConsentDisclaimer";
import { ConsentToTreatment } from "@/components/Consent/ConsentToTreatment";
import { getCurrentPolicyVersion } from "@/lib/helpers/getCurrentPolicyVersion";
import { Button2 } from "@/ui2/atoms/Button";
import { Card2 } from "@/ui2/atoms/Card";
import { Heading2 } from "@/ui2/atoms/Heading";
import { getAutomationDataAttr } from "@/utils/automationUtils";

const customDisclaimerPrefix = "By selecting “Agree & Continue”, you agree to aptihealth’s";

type ConsentProps = {
  onSubmit: (response?: boolean) => Promise<void>;
  policyMetadata: PolicyMetaData;
  subTitle: string;
  title: string;
} & Omit<ComponentProps<typeof Card2>, "onSubmit">;

export function ConsentToPolicy({ onSubmit, policyMetadata, subTitle, title }: ConsentProps) {
  const [hasConsented, setHasConsented] = useState(false);
  const policyVersion = getCurrentPolicyVersion({ policy: "CONSENT_TO_TREATMENT", policyMetadata });

  return (
    <Card2 className="tw:p-12 tw:flex-col tw:gap-8 tw:lg:max-w-148 tw:mx-auto">
      <Heading2 as="h1" className="tw:text-center" size={4}>
        {title}
      </Heading2>

      <p className="body-md" {...getAutomationDataAttr("consent-on-log-in-subtitle")}>
        {subTitle}
      </p>

      <ConsentToTreatment
        consentEvent={() => setHasConsented((prevState) => !prevState)}
        version={policyVersion}
      />

      <Button2
        className="tw:w-full"
        isDisabled={!hasConsented}
        onPress={() => onSubmit(true)}
        {...getAutomationDataAttr("consent-on-log-in-agree-and-continue-button")}
      >
        Agree & Continue
      </Button2>

      <ConsentDisclaimer
        customDisclaimerPrefix={customDisclaimerPrefix}
        policyMetadata={policyMetadata}
      />
    </Card2>
  );
}
